import { useState, type FC } from 'react';
import { createRefetchContainer, graphql, type RelayRefetchProp } from 'react-relay/legacy.js';
import { handleUserRefetch } from '../helpers/sbCreatorsRootUserRefetch.ts';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes.ts';

/* Components */
import { SbRespCreatorsComponents as SbRespCreatorsRootComponents } from '../SbRespCreatorsComponents/SbRespCreatorsComponents.jsx';
import { SbSharedHead } from '../SbSharedHead/SbSharedHead.tsx';
import {
    RecentlyViewedItemsProvider,
    TABLET,
    DESKTOP,
} from 'dibs-recent-history/exports/RecentlyViewedItemsProvider.ts';
import { SbSharedFavoritesNudgeToast } from '../../shared/SharedFavoritesNudge/SbSharedFavoritesNudgeToast/SbSharedFavoritesNudgeToast.tsx';

/* Hooks */
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useSharedUserAndRelayVarsLoader } from '../../shared/SharedUserAndRelayVarLoader.tsx';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage.ts';
import { useSbSharedPageTracking } from '../hooks/useSbSharedPageTracking.ts';
import { sbRespRefineMenuFiltersMap } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap.ts';

import { type SbRespCreatorsLayout_viewer$data } from './__generated__/SbRespCreatorsLayout_viewer.graphql.ts';

type Props = {
    viewer: SbRespCreatorsLayout_viewer$data;
    reinitTracking?: () => void;
    relay: RelayRefetchProp;
};

const SbRespCreatorsLayoutComponent: FC<Props> = props => {
    const [isClient, setIsClient] = useState(false);
    const { isTablet } = useServerVarsContext();
    const { viewer, reinitTracking, relay } = props;
    const user = viewer.user || null;
    const userId = user?.serviceId ?? '';
    const { itemSearch } = viewer;
    useSharedUserAndRelayVarsLoader({
        onUserRefetch: handleUserRefetch(relay, setIsClient),
    });

    useDwelledOnPage(PAGE_TYPE.SEARCH);
    useSbSharedPageTracking({
        itemSearch,
        user,
        reinitTracking,
        refineMenuFiltersMap: sbRespRefineMenuFiltersMap,
    });

    const { isItemFavoritingFeb2026Variant, isItemFavoritingFeb2026Control } =
        useServerVarsContext();

    return (
        <RecentlyViewedItemsProvider deviceType={isTablet ? TABLET : DESKTOP}>
            <SbSharedHead itemSearch={itemSearch} />
            <SbRespCreatorsRootComponents
                viewer={viewer}
                itemSearch={itemSearch}
                user={user}
                isClient={isClient}
            />
            <SbSharedFavoritesNudgeToast
                userId={userId}
                isItemFavoritingFeb2026Variant={isItemFavoritingFeb2026Variant}
                isItemFavoritingFeb2026Control={isItemFavoritingFeb2026Control}
            />
        </RecentlyViewedItemsProvider>
    );
};

export const SbRespCreatorsLayout = createRefetchContainer(
    SbRespCreatorsLayoutComponent,
    {
        viewer: graphql`
            fragment SbRespCreatorsLayout_viewer on Viewer {
                ...SbRespCreatorsComponents_viewer
                user(userId: $userId) @include(if: $fetchUser) {
                    serviceId
                    ...useSbSharedPageTracking_user
                    ...SbRespCreatorsComponents_user
                }
                itemSearch(
                    userId: $personalizationId
                    guestId: $guestId
                    uriRef: $uriRef
                    originalUrl: $originalUrl
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    regions: $regions
                    disableForceFacet: $disableForceFacet
                    disableNonParameterizedUrlRedirects: $disableNonParameterizedUrlRedirects
                    userCountryCode: $userCountryCode
                    regionsList: $regionsList
                    priceBookName: $priceBookName
                ) @required(action: THROW) {
                    ...SbSharedHead_itemSearch
                    ...SbRespCreatorsComponents_itemSearch
                    ...SbSharedRefetchContainer_itemSearch
                    ...useSbSharedPageTracking_itemSearch
                }
            }
        `,
    },
    graphql`
        query SbRespCreatorsLayoutRefetchQuery(
            $userId: String = ""
            $fetchUser: Boolean!
            $followSearchPages: [String]
            $followSearchTypes: [types] = [SEARCH, USER_PREFERENCE]
        ) {
            viewer {
                user(userId: $userId) @include(if: $fetchUser) {
                    serviceId
                    ...useSbSharedPageTracking_user
                    ...SbRespCreatorsComponents_user
                }
            }
        }
    `
);
